"use client";

import { useEffect, useOptimistic, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronDownIcon, SearchIcon } from "lucide-react";

import { useTranslator } from "@/lib/translation-context";

import {
  Button,
  Checkbox,
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
  Input,
  RadioGroup,
  RadioGroupItem,
  Slider,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@tripcart/traveler-ui";

import {
  DURATION_BANDS,
  MONTH_LABELS,
  PRICE_DISPLAY_MAX,
  PRICE_MIN,
  PRICE_STEP,
  type ExperienceSearchState,
  parseState,
  splitCsv,
} from "./_filters-shared";
import {
  formatFacetToken,
  type SearchFilterBarDto,
  type SearchFilterBarItemDto,
} from "@tripcart/shared";
import type { Translator } from "@/lib/translations";
import { useFormatMajorMoney } from "@/lib/currency-format-context";
import { scrollToResultsTopIfScrolledPast } from "./_scroll";

// Not `styles`: that name is FilterPanel's style-facet prop.
import css from "./_filter-panel.client.module.css";

export interface FilterOption {
  slug: string;
  name: string;
  count: number;
  /** The parent this value hangs off, for facets the operator arranged as a
   *  tree (destinations). Absent on a root, and on flat facets like activity
   *  type. Drives the indent in `OptionList` — without it "Asia", "Nepal" and
   *  "Kathmandu" all read as siblings when Kathmandu is inside Nepal inside
   *  Asia. */
  parentSlug?: string | null;
}

/** Order a facet list so each child follows its parent, and indent by depth.
 *
 *  Roots keep the incoming order (busiest first). A child is placed directly
 *  under its parent rather than by its own count, because the list is read as
 *  a hierarchy — "Kathmandu" three rows below "Nepal" tells the traveler
 *  nothing about the two being related.
 *
 *  A value whose parent is not in the list (filtered out for having no tours,
 *  or on another page of the facet) is treated as a root: it must still be
 *  selectable, and indenting it under a parent nobody can see would be a lie.
 */
export function nestOptions(
  options: FilterOption[],
): Array<FilterOption & { depth: number }> {
  const bySlug = new Map(options.map((o) => [o.slug, o]));
  const children = new Map<string, FilterOption[]>();
  const roots: FilterOption[] = [];
  for (const o of options) {
    const parent = o.parentSlug;
    if (parent && bySlug.has(parent) && parent !== o.slug) {
      const list = children.get(parent) ?? [];
      list.push(o);
      children.set(parent, list);
    } else {
      roots.push(o);
    }
  }
  const out: Array<FilterOption & { depth: number }> = [];
  const walk = (node: FilterOption, depth: number, seen: Set<string>): void => {
    if (seen.has(node.slug)) return; // a cycle in operator data must not hang
    seen.add(node.slug);
    out.push({ ...node, depth });
    for (const child of children.get(node.slug) ?? []) {
      walk(child, depth + 1, seen);
    }
  };
  const seen = new Set<string>();
  for (const root of roots) walk(root, 0, seen);
  // Anything the walk never reached — every member of a parent cycle, where
  // nothing qualifies as a root — is emitted flat. A filter the traveler can
  // see and use beats a tidy tree that silently drops values.
  //
  // Pushed directly rather than through `walk`: walking would recurse into the
  // rest of the cycle and indent it, so with A→B→A the list showed B nested
  // under A — a parent/child relationship that does not exist, and exactly the
  // lie the docstring above refuses for a value whose parent is absent. In a
  // cycle there is no correct nesting, so there is none.
  //
  // KNOWN LIMIT: this flattens healthy branches hanging off a cycle too, not
  // only the cycle itself. With A→B→A plus C whose parent is A, C is never
  // reached by the walk (A is not a root) and so is emitted at depth 0, losing
  // a nesting relationship that genuinely exists. Separating the two needs a
  // per-node "is this slug on a cycle?" test, and the branch is unreachable
  // from the operator UI — a destination cannot be made its own ancestor — so
  // the cost is not paid here. Every value is still shown and still
  // selectable, which is the property that matters.
  for (const o of options) {
    if (seen.has(o.slug)) continue;
    seen.add(o.slug);
    out.push({ ...o, depth: 0 });
  }
  return out;
}

const SHOW_MORE_LIMIT = 5;

/**
 * The bar to draw when the operator has arranged nothing — an agency without
 * the Search Filters app (AC-01), or a definition that failed to load. The five
 * the platform ships, in the order it ships them.
 *
 * `label` here is never displayed: `renamed` is false and every one of these
 * slugs has our own translated heading in `headingFor`. It carries the seeded
 * name so the shape is a real `SearchFilterBarItemDto` rather than a lie.
 */
const DEFAULT_BAR: readonly SearchFilterBarItemDto[] = [
  {
    slug: "duration",
    source: "builtin",
    label: "Duration",
    renamed: false,
    options: [],
  },
  {
    slug: "price",
    source: "builtin",
    label: "Price",
    renamed: false,
    options: [],
  },
  {
    slug: "date",
    source: "builtin",
    label: "Date",
    renamed: false,
    options: [],
  },
  {
    slug: "destination",
    source: "taxonomy",
    label: "Destinations",
    renamed: false,
    options: [],
  },
  {
    slug: "activity",
    source: "taxonomy",
    label: "Activity Types",
    renamed: false,
    options: [],
  },
];

/**
 * Which sections start expanded. Keyed by slug rather than by position, so the
 * default bar opens exactly the three it always opened no matter where the
 * operator later drags them. A filter with no entry — every custom one — starts
 * open, because an operator who just created a filter needs to see it working.
 */
const OPEN_ON_FIRST_PAINT: Record<string, boolean> = {
  duration: true,
  price: true,
  date: false,
  destination: true,
  activity: false,
};

export function FilterPanel({
  destinations,
  styles,
  idPrefix = "f",
  catalogMaxPrice,
  bar,
}: {
  destinations: FilterOption[];
  styles: FilterOption[];
  idPrefix?: string;
  catalogMaxPrice?: number;
  /** The operator's arrangement. Absent (a failed fetch) falls back to
   *  `DEFAULT_BAR`, which is the bar this page drew before the app existed. */
  bar?: SearchFilterBarDto;
}): React.ReactElement {
  const t = useTranslator();
  const router = useRouter();
  const sp = useSearchParams();
  const [, startTransition] = useTransition();

  /**
   * The filters read their state from an OPTIMISTIC copy of the query string,
   * not from the committed URL.
   *
   * Every control here is fully controlled off the URL, and the URL only moves
   * when the navigation commits — which means a server round-trip for the new
   * tour list. Inside a transition React deliberately keeps the old UI on
   * screen until then (that is what stops the results flashing a skeleton), so
   * the tick in the checkbox was waiting on the server too: measured at
   * 150-190ms locally against a warm route and a local database, and worse
   * over a real network. React also re-renders the input back to `checked={on}`
   * on every pass, so even the browser's own instant toggle was being undone.
   *
   * `useOptimistic` gives the controls the state the operator just asked for,
   * immediately, and React drops the overlay once the real URL catches up. The
   * results list keeps its old-content-during-transition behaviour untouched.
   */
  const [optimisticQuery, applyOptimisticQuery] = useOptimistic(sp.toString());
  const optimisticParams = new URLSearchParams(optimisticQuery);
  const state = parseState((k) => optimisticParams.get(k) ?? undefined);

  function commit(mutate: (p: URLSearchParams) => void): void {
    // Built from the optimistic query, not `sp`: two quick clicks would
    // otherwise both branch off the pre-click URL and the first filter would
    // be dropped by the second.
    const p = new URLSearchParams(optimisticQuery);
    mutate(p);
    p.delete("page");
    const next = p.toString();
    // Transition so applying a filter keeps the current results on screen
    // instead of flashing the skeleton while the server returns the new page.
    // `applyOptimisticQuery` must be inside it — React only shows an
    // optimistic value while its transition is pending.
    startTransition(() => {
      applyOptimisticQuery(next);
      router.replace(`?${next}`, { scroll: false });
    });
    // `scroll: false` above is what keeps React's transition from jumping the
    // page; this puts the traveller back at the top of a list that just
    // changed under them, and only when they had scrolled past it.
    scrollToResultsTopIfScrolledPast();
  }
  function setParam(key: string, value: string | null): void {
    commit((p) => {
      if (!value) p.delete(key);
      else p.set(key, value);
    });
  }
  function toggleCsv(key: string, value: string): void {
    commit((p) => {
      const set = new Set(splitCsv(p.get(key)));
      if (set.has(value)) set.delete(value);
      else set.add(value);
      const arr = [...set];
      if (arr.length) p.set(key, arr.join(","));
      else p.delete(key);
    });
  }

  const effectiveMax = catalogMaxPrice ?? PRICE_DISPLAY_MAX;

  // `arranged`, not `filters.length` — an operator who hid every filter has
  // arranged an empty bar, and reading emptiness as "unconfigured" would put
  // all five back. Absent `bar` is a failed fetch (see the prop's doc).
  const items = bar?.arranged ? bar.filters : DEFAULT_BAR;
  const control: ControlContext = {
    state,
    destinations,
    styles,
    idPrefix,
    effectiveMax,
    isMaxSet: Boolean(optimisticParams.get("priceMax")),
    setParam,
    toggleCsv,
    commit,
  };

  return (
    <div>
      {items.map((item) => {
        const rendered = renderControl(item, control, t);
        // A platform filter this build has no renderer for is omitted, not
        // drawn as an empty box.
        if (rendered === null) return null;
        return (
          <FilterSection
            key={item.slug}
            label={headingFor(item, t)}
            defaultOpen={OPEN_ON_FIRST_PAINT[item.slug] ?? true}
          >
            {rendered}
          </FilterSection>
        );
      })}
    </div>
  );
}

/** Everything a control needs from the panel, bundled so each renderer takes
 *  the item and this — not nine positional arguments. */
interface ControlContext {
  state: ExperienceSearchState;
  destinations: FilterOption[];
  styles: FilterOption[];
  idPrefix: string;
  effectiveMax: number;
  isMaxSet: boolean;
  setParam: (key: string, value: string | null) => void;
  toggleCsv: (key: string, value: string) => void;
  commit: (mutate: (p: URLSearchParams) => void) => void;
}

/**
 * The heading above one filter.
 *
 * An operator's own wording is shown verbatim and never translated — it is
 * their copy, not ours. Our own headings for the five platform filters go
 * through `t()` as literals so `pnpm i18n` keeps seeing them, which is why this
 * is a switch and not a lookup table.
 */
function headingFor(item: SearchFilterBarItemDto, t: Translator): string {
  if (item.source === "custom" || item.renamed) return item.label;
  switch (item.slug) {
    case "duration":
      return t("Duration");
    case "price":
      return t("Price");
    case "date":
      return t("Date");
    case "destination":
      return t("Destination");
    case "activity":
      return t("Activity Type");
  }
  // A platform slug newer than this build — the server's own name beats nothing.
  return item.label;
}

/**
 * `null` means "this build cannot draw that filter", which drops the section.
 *
 * The five platform slugs keep the params they have always used (`dur`,
 * `priceMin`/`priceMax`, `months`, `dest`, `style`); only `custom` filters ride
 * `facet`, because the API lets a `destination` param overwrite a
 * `facet=destination:…` entry and one of the two would silently win.
 */
function renderControl(
  item: SearchFilterBarItemDto,
  ctx: ControlContext,
  t: Translator,
): React.ReactElement | null {
  switch (item.slug) {
    case "duration":
      return (
        <DurationBands
          selected={ctx.state.dur}
          idPrefix={ctx.idPrefix}
          onSelect={(id) => ctx.setParam("dur", id)}
        />
      );
    case "price":
      return (
        <PriceRange
          min={ctx.state.priceMin}
          max={ctx.state.priceMax}
          ceiling={ctx.effectiveMax}
          isMaxSet={ctx.isMaxSet}
          onCommit={(lo, hi) =>
            ctx.commit((p) => {
              if (lo > PRICE_MIN) p.set("priceMin", String(lo));
              else p.delete("priceMin");
              if (hi < ctx.effectiveMax) p.set("priceMax", String(hi));
              else p.delete("priceMax");
            })
          }
        />
      );
    case "date":
      return (
        <DateMonths
          selected={ctx.state.months}
          onToggle={(m) => ctx.toggleCsv("months", m)}
        />
      );
    case "destination":
      return (
        <CheckboxList
          placeholder={t("Search destination…")}
          options={ctx.destinations}
          selected={ctx.state.dest}
          onToggle={(slug) => ctx.toggleCsv("dest", slug)}
        />
      );
    case "activity":
      return (
        <CheckboxList
          placeholder={t("Search activity type…")}
          options={ctx.styles}
          selected={ctx.state.style}
          onToggle={(slug) => ctx.toggleCsv("style", slug)}
        />
      );
  }
  if (item.source !== "custom") return null;
  return (
    <CheckboxList
      // Only long lists earn a search box; a three-option Difficulty does not.
      {...(item.options.length > SHOW_MORE_LIMIT
        ? { placeholder: t("Search options…") }
        : {})}
      options={item.options.map((o) => ({
        slug: o.slug,
        name: o.label,
        // Zero is shown, never filtered out: an option nobody has assigned yet
        // reads as a bug to the operator who just created it.
        count: o.count,
      }))}
      selected={ctx.state.facets[item.slug] ?? []}
      onToggle={(slug) =>
        ctx.toggleCsv("facet", formatFacetToken(item.slug, slug))
      }
    />
  );
}

function DurationBands({
  selected,
  idPrefix,
  onSelect,
}: {
  selected: string | null;
  idPrefix: string;
  onSelect: (id: string | null) => void;
}): React.ReactElement {
  const t = useTranslator();
  return (
    <RadioGroup
      aria-label={t("Duration")}
      name={`${idPrefix}-dur`}
      // "" matches no band, so nothing reads as picked.
      value={selected ?? ""}
      onValueChange={(id) => onSelect(id)}
      className={css.optionList}
    >
      {DURATION_BANDS.map((band) => (
        <label
          key={band.id}
          // A radio cannot clear itself, so a second click on the picked band
          // does. Every click on the row, radio or text, reaches the radio's
          // hidden <input> exactly once; acting only there keeps it one commit.
          onClick={(e) => {
            if (selected === band.id && e.target instanceof HTMLInputElement) {
              onSelect(null);
            }
          }}
          className={css.option}
        >
          <RadioGroupItem value={band.id} />
          <span className={css.optionLabel}>{band.label}</span>
        </label>
      ))}
    </RadioGroup>
  );
}

/**
 * One collapsible filter facet. Base UI Collapsible, not native `<details>`:
 * `::details-content` animates only in Chromium, and this animates the height
 * in every browser. Facets are independent, so per-section Collapsible rather
 * than Accordion (no coupled arrow-nav).
 */
function FilterSection({
  label,
  defaultOpen = false,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Collapsible defaultOpen={defaultOpen} className={css.section}>
      <CollapsibleTrigger className={css.summary}>
        {label}
        <ChevronDownIcon className={css.sectionChevron} aria-hidden />
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className={css.sectionBody}>{children}</div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

function PriceRange({
  min,
  max,
  ceiling,
  isMaxSet,
  onCommit,
}: {
  min: number;
  max: number;
  ceiling: number;
  isMaxSet: boolean;
  onCommit: (lo: number, hi: number) => void;
}): React.ReactElement {
  const t = useTranslator();
  const [lo, setLo] = useState(min);
  const [hi, setHi] = useState(isMaxSet ? Math.min(max, ceiling) : ceiling);
  // Re-sync when the URL changes externally (e.g. Clear all).
  useEffect(() => {
    setLo(min);
    setHi(isMaxSet ? Math.min(max, ceiling) : ceiling);
  }, [min, max, ceiling, isMaxSet]);

  const formatMajorMoney = useFormatMajorMoney();
  const GAP = 50;
  const fmt = (n: number) =>
    n >= ceiling ? `${formatMajorMoney(ceiling)}+` : formatMajorMoney(n);

  return (
    <div>
      <div className={css.priceValues}>
        <span className={css.priceValue}>{fmt(lo)}</span>
        <span className={css.priceDash}>—</span>
        <span className={css.priceValue}>{fmt(hi)}</span>
      </div>
      <Slider
        thumbLabels={[t("Minimum Price"), t("Maximum Price")]}
        min={PRICE_MIN}
        max={ceiling}
        step={PRICE_STEP}
        minStepsBetweenValues={GAP / PRICE_STEP}
        value={[lo, hi]}
        onValueChange={(value) => {
          const [nextLo, nextHi] = value as readonly number[];
          setLo(nextLo!);
          setHi(nextHi!);
        }}
        onValueCommitted={(value) => {
          const [nextLo, nextHi] = value as readonly number[];
          onCommit(nextLo!, nextHi!);
        }}
        className={css.slider}
      />
      <div className={css.priceScale}>
        <span>{formatMajorMoney(0)}</span>
        <span>{formatMajorMoney(ceiling)}+</span>
      </div>
    </div>
  );
}

function DateMonths({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (month: string) => void;
}): React.ReactElement {
  const today = new Date();
  const thisYear = today.getFullYear();
  const thisMonth = today.getMonth();
  const years = [thisYear, thisYear + 1, thisYear + 2];
  const [activeYear, setActiveYear] = useState(years[0]!);
  const selectedSet = new Set(selected);

  return (
    <Tabs
      value={activeYear}
      onValueChange={(value) => setActiveYear(value as number)}
      className={css.years}
    >
      <TabsList variant="line" className={css.yearList}>
        {years.map((y) => (
          <TabsTrigger key={y} value={y} className={css.yearTrigger}>
            {y}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value={activeYear} className={css.monthGrid}>
        {MONTH_LABELS.map((label, i) => {
          if (activeYear === thisYear && i < thisMonth) return null;
          const key = `${activeYear}-${i}`;
          const on = selectedSet.has(key);
          return (
            <Button
              key={key}
              variant="outline"
              size="sm"
              pressed={on}
              onClick={() => onToggle(key)}
              className={css.month}
            >
              {label}
            </Button>
          );
        })}
      </TabsContent>
    </Tabs>
  );
}

function CheckboxList({
  placeholder,
  options,
  selected,
  onToggle,
}: {
  /** Omitted for lists short enough that searching them is pointless — its
   *  presence is what puts the search box on screen. */
  placeholder?: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (slug: string) => void;
}): React.ReactElement {
  const t = useTranslator();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const selectedSet = new Set(selected);

  // Nest first, then search. Searching a nested list would otherwise strand a
  // matched child under a parent the query removed; a flat result is the right
  // answer while a query is active, and the tree returns when it is cleared.
  const nested = nestOptions(options);
  const filtered = query
    ? nested
        .filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
        .map((o) => ({ ...o, depth: 0 }))
    : nested;
  const visible = expanded ? filtered : filtered.slice(0, SHOW_MORE_LIMIT);
  const hiddenCount = filtered.length - visible.length;

  return (
    <div>
      {placeholder ? (
        <div className={css.search}>
          <SearchIcon className={css.searchIcon} strokeWidth={2} aria-hidden />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className={css.searchInput}
          />
        </div>
      ) : null}
      <div className={css.optionList}>
        {visible.map((o) => {
          const on = selectedSet.has(o.slug);
          return (
            <label
              key={o.slug}
              // Indent marks the level. Capped at two so a deep tree cannot
              // squeeze the label out of a 17rem column.
              style={{
                paddingLeft: `${1 + Math.min(o.depth, 2) * 0.85}rem`,
              }}
              className={css.option}
            >
              <Checkbox checked={on} onCheckedChange={() => onToggle(o.slug)} />
              <span className={css.optionLabel}>{o.name}</span>
              {/* Size is the card's original 12px. `ink-muted` rather than
                  `ink-subtle` stays: `packages/ui/CLAUDE.md` §2.2 reserves
                  subtle for icons and dividers because it clears only the 3:1
                  non-text floor, and a count is text a person reads. That is a
                  contrast fix, not a type-scale one. */}
              <span className={css.optionCount}>{o.count}</span>
            </label>
          );
        })}
        {filtered.length === 0 ? (
          <p className={css.noMatches}>{t("No Matches")}</p>
        ) : null}
      </div>
      {!query && filtered.length > SHOW_MORE_LIMIT ? (
        <Button
          variant="link"
          size="sm"
          onClick={() => setExpanded((e) => !e)}
          className={css.showMore}
        >
          {expanded ? t("Show Less") : t("Show {n} More", { n: hiddenCount })}
        </Button>
      ) : null}
    </div>
  );
}
